/**
 * Global "the user is dictating" beacon.
 *
 * Voice dictation (AI voice or traditional) produces no mouse/keyboard events,
 * so SessionExpiryManager's DOM-activity heuristic would treat a radiologist
 * who is speaking (but not touching the mouse) as idle and pop the expiry modal
 * mid-dictation. Each dictation flow reports while it is capturing/processing
 * audio so the session is kept alive silently instead.
 *
 * A counter (not a boolean) so overlapping sources — e.g. traditional capture
 * followed by its streaming phase, or two hooks mounted at once — compose safely.
 */
let activeCount = 0;

/** Suscriptores avisados cuando la última fuente activa se libera. */
const idleListeners = new Set<() => void>();

/**
 * Mark one dictation source as busy. Returns a cleanup that clears it exactly
 * once (safe to call from a React effect cleanup or an imperative teardown).
 *
 * Las fuentes se solapan a propósito: el traspaso entre fases (grabación →
 * finalización → procesado → generación) siempre se hace tomando la siguiente
 * ficha ANTES de soltar la anterior, de forma que el contador nunca cae a 0 a
 * mitad de un dictado. Así no hace falta ninguna ventana temporal de gracia.
 */
export function beginDictationActivity(): () => void {
  activeCount += 1;
  let cleared = false;
  return () => {
    if (cleared) return;
    cleared = true;
    activeCount = Math.max(0, activeCount - 1);
    if (activeCount === 0) {
      // Copia: un listener puede resuscribirse o abrir otra actividad.
      for (const listener of [...idleListeners]) listener();
    }
  };
}

/** True while any dictation flow is actively capturing or processing audio. */
export function isDictationActive(): boolean {
  return activeCount > 0;
}

/**
 * Avisa cuando NO queda ninguna fuente activa. Permite aplazar un trabajo
 * (p. ej. el autosave por inactividad) hasta que el dictado termine sin
 * recurrir a un temporizador que sondee `isDictationActive()`.
 * Devuelve la función para darse de baja.
 */
export function subscribeDictationIdle(listener: () => void): () => void {
  idleListeners.add(listener);
  return () => { idleListeners.delete(listener); };
}
